/**
 * Google Analytics 4 (gtag.js), kept by the owner's decision. The page loads
 * gtag.js itself (async, from googletagmanager.com, allowed by the CSP) when
 * <body data-analytics-id> is set: production pages only, and never a page
 * whose URL carries a secret (the password reset link). This module feeds its
 * dataLayer. GA4 event names take letters, digits and underscores only.
 *
 * Events: sign_up_click, go_billing, convert_any plus the tool's own
 * convert_* event for every finished conversion, download_any for every
 * download from the Inbox. The payment (buy_success) happens on FastSpring;
 * the server sends that one (app/docszone/analytics.py).
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

type ConversionEvent =
  | "convert_to_pdf"
  | "convert_combine_to_pdf"
  | "convert_web_to_pdf"
  | "convert_pdf_to_word"
  | "convert_pdf_to_excel"
  | "convert_pdf_to_jpg"
  | "convert_ocr";

export type AnalyticsEvent =
  | "sign_up_click"
  | "go_billing"
  | "convert_any"
  | ConversionEvent
  | "download_any";

// the tools of the converter pages (data-tool), named after their pages
const CONVERSION_EVENTS: ReadonlyMap<string, ConversionEvent> = new Map([
  ["createpdf", "convert_to_pdf"],
  ["combinetopdf", "convert_combine_to_pdf"],
  ["webtopdf", "convert_web_to_pdf"],
  ["pdftoword", "convert_pdf_to_word"],
  ["pdftoexcel", "convert_pdf_to_excel"],
  ["pdftojpg", "convert_pdf_to_jpg"],
  ["ocr", "convert_ocr"],
]);

let enabled = false;

function gtag(..._args: unknown[]): void {
  // gtag.js reads the Arguments object of this call from the dataLayer (arrays are ignored)
  // eslint-disable-next-line prefer-rest-params -- see above
  window.dataLayer?.push(arguments);
}

export function initAnalytics(): void {
  const id = document.body.dataset["analyticsId"];
  if (id === undefined || id === "") return;
  window.dataLayer ??= [];
  enabled = true;
  gtag("js", new Date());
  gtag("config", id);
}

export function trackEvent(name: AnalyticsEvent): void {
  if (enabled) gtag("event", name);
}

/** A finished conversion: convert_any and the event of its tool. */
export function trackConversion(tool: string): void {
  trackEvent("convert_any");
  const event = CONVERSION_EVENTS.get(tool);
  if (event !== undefined) trackEvent(event);
}
